import { useState } from 'react';
import { useRouter } from '@/lib/navigation';
import { useSession } from '@/contexts/SessionContext';
import { useQueryClient } from '@tanstack/react-query';
import { isFeatureOn, useFeatureRuntime } from '@septima/feature-core/react';
import {
    sidebarRowsFor,
    groupSectionsForSidebar,
    switcherButtonsFor,
    FRONTENDS,
} from '@/lib/frontends';
import { grantedShells, isActivePersona, type StaffShellKey } from '@septima/contract';
import { switchActingFor } from '@/lib/auth';
import { runBookSwitch } from '@/lib/persona-switch';
import { useToast } from '@/lib/useToast';
// Direct component imports — not the `@/components/ui` barrel — so the
// always-mounted shell does not drag charting (Sparkline) and other heavy
// barrel exports into every staff route (PRD #156).
import Sidebar, { SidebarMenu } from '@/components/ui/Sidebar';
import SidebarRow, { type SidebarBadgeTone } from '@/components/ui/SidebarRow';
import AgentBadge from '@/components/agent/AgentBadge';
import SignOutButton from '@/components/ui/SignOutButton';
import Image from '@/lib/Image';
import { getSeptimaMarkPath, getSeptimaWordmarkPath } from '@/lib/logos';
import { useTheme } from '@/lib/ThemeContext';
import { useDialog } from '@/lib/useDialog';
import { useReviewQueueCounts } from '@/lib/use-review-queue-counts';
import { useNavCounts } from '@/lib/nav/use-nav-counts';
import { cn, initialsOf } from '@/lib/utils';
import { track } from '@/lib/analytics/client';
import {
    BookOpen,
    BookUser,
    PieChart,
    CheckSquare,
    Building2,
    Phone,
    Shield,
    Users,
    FileText,
    FileCheck,
    TrendingUp,
    Settings,
    LayoutDashboard,
    Scale,
    ArrowRightLeft,
    ArrowLeftRight,
    Wallet,
    Receipt,
    Plug,
    FileBarChart,
    Heart,
    ClipboardCheck,
    UserCheck,
    AlertTriangle,
    GraduationCap,
    FileWarning,
    Calendar,
    Landmark,
    Building,
    LineChart,
    Activity,
    UsersRound,
    Rocket,
    AlertOctagon,
    BarChart3,
    Presentation,
    Circle,
    Bot,
    ShieldCheck,
    BrainCircuit,
    Workflow,
    CircleUser,
    Target,
    ScrollText,
    Contact,
    Upload,
    RefreshCw,
    HeartPulse,
    UserX,
    Library,
    Sliders,
    KeyRound,
    Package,
    Network,
    Globe,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface AppSidebarProps {
    frontend: StaffShellKey;
    mobileOpen?: boolean;
    onMobileClose?: () => void;
    /** True when the reader collapsed the sidebar to the icon rail. */
    collapsed?: boolean;
    onToggleCollapsed?: () => void;
    /** The toggle shortcut, for the tooltip of the toggle button. */
    shortcutLabel?: string;
}

// Per-frontend accent alias so the active switcher button and the avatar tint
// with the persona colour through one token rather than four branches. It is a
// class, so the sidebar needs no inline style.
const SIDEBAR_ACCENT: Record<StaffShellKey, string> = {
    desk: '[--sidebar-accent:var(--color-persona-desk)]',
    ops: '[--sidebar-accent:var(--color-persona-ops)]',
    comply: '[--sidebar-accent:var(--color-persona-comply)]',
    view: '[--sidebar-accent:var(--color-persona-view)]',
    // The Admin shell is not a persona, so it takes the accent of the brand.
    admin: '[--sidebar-accent:var(--color-accent)]',
};

// Icon mapping from name to component
const iconMap: Record<string, LucideIcon> = {
    BookOpen,
    BookUser,
    PieChart,
    CheckSquare,
    Building2,
    Phone,
    Shield,
    Users,
    FileText,
    FileCheck,
    TrendingUp,
    Settings,
    LayoutDashboard,
    Scale,
    ArrowRightLeft,
    ArrowLeftRight,
    Wallet,
    Receipt,
    Plug,
    FileBarChart,
    Heart,
    ClipboardCheck,
    UserCheck,
    AlertTriangle,
    GraduationCap,
    FileWarning,
    Calendar,
    Landmark,
    Building,
    LineChart,
    Activity,
    UsersRound,
    Rocket,
    AlertOctagon,
    BarChart3,
    Presentation,
    Circle,
    Bot,
    ShieldCheck,
    BrainCircuit,
    Workflow,
    CircleUser,
    Target,
    ScrollText,
    Contact,
    Network,
    Globe,
    Upload,
    RefreshCw,
    HeartPulse,
    UserX,
    Library,
    Sliders,
    KeyRound,
    Package,
};

// Helper to get icon component from name
function getIcon(name: string): LucideIcon {
    return iconMap[name] || Circle;
}

/**
 * A live badge, or nothing at all when the count is zero. `SidebarRow` renders
 * any badge it is given, including a "0" pill, so the suppression belongs here
 * — an empty queue must show no badge, not a zero.
 */
function badgeOf(
    count: number | undefined,
    tone: SidebarBadgeTone,
): { count: number; tone: SidebarBadgeTone } | undefined {
    return count !== undefined && count > 0 ? { count, tone } : undefined;
}

/**
 * The shells that carry a live nav-count badge, and so poll `/api/nav/counts`.
 * They are the three onboarding-pipeline teams; `view` is oversight and owns no
 * stage, so it has nothing to be told about. The endpoint admits exactly this
 * set (plus `im-assistant`, which runs inside the Desk shell).
 */
const BADGED_FRONTENDS: readonly StaffShellKey[] = ['desk', 'ops', 'comply'];

export default function AppSidebar({
    frontend,
    mobileOpen,
    onMobileClose,
    collapsed: collapsedProp = false,
    onToggleCollapsed,
    shortcutLabel,
}: AppSidebarProps) {
    const router = useRouter();
    useDialog({ isOpen: !!mobileOpen, onClose: onMobileClose ?? (() => {}) });
    const { user, refresh, setActingForUserId } = useSession();
    const queryClient = useQueryClient();
    const { pushToast } = useToast();
    const actingFor = user?.actingFor ?? [];
    const activeActingForUserId = user?.activeActingForUserId ?? null;
    // True while the acting-for request is in flight. It disables the whole
    // switcher row, so the reader cannot start a second switch on top of a
    // switch that the server has not confirmed yet.
    const [bookSwitchInFlight, setBookSwitchInFlight] = useState(false);
    const { resolvedTheme } = useTheme();
    // The sidebar rows are decided by the ACTIVE persona (ADR-0004): on the Desk
    // shell that may be `desk` or `im-assistant` (the latter omits the
    // suitability + vulnerability rows). The Admin shell asks the granted
    // tokens only. Fall back to the frontend prop when the session carries no
    // (or a stale) active persona.
    const activePersona =
        user?.activePersona && isActivePersona(user.activePersona)
            ? user.activePersona
            : frontend === 'admin'
              ? 'view'
              : frontend;
    // A row of a feature that needs an AI seat shows only when a seat of the
    // user covers it (ADR-0027). The build holds the enabled features only.
    const featureRuntime = useFeatureRuntime();
    const sections = sidebarRowsFor(frontend, activePersona, user?.personas ?? []).filter(
        (section) => !section.feature || isFeatureOn(featureRuntime, section.feature),
    );
    const groupedSections = groupSectionsForSidebar(frontend, sections);

    // The switcher row: one button for each granted view, one for each IM book
    // an assistant acts for in place of the single Desk button, and the Admin
    // button for a user who holds the `admin` token.
    const switcherButtons = switcherButtonsFor(
        grantedShells(user?.personas ?? []),
        frontend,
        actingFor,
        activeActingForUserId,
        user?.userId ?? null,
    );

    // The drawer below lg always shows the labels, so the rail applies only
    // to the inline sidebar.
    const collapsed = collapsedProp && !mobileOpen;

    // The 'Agents' sidebar row carries the unified pending-review count —
    // AgentTask review_required + persisted artefact rows pending review —
    // since review now happens on the Agents Kanban's "Needs Review" column.
    // Backed by the lightweight counts endpoint (not the full queue model)
    // so the badge does not load the entire pending payload into chrome.
    const { itemCount: reviewItemCount } = useReviewQueueCounts(
        frontend === 'admin' ? undefined : frontend,
    );

    // The three work counts, from their own lightweight endpoint for the same
    // reason (SEP-210). The Onboarding badge belongs to all three pipeline
    // teams (SEP-208) — Ops needs to see a case land on Account opening — while
    // the two Desk rows read zero elsewhere. View is oversight with no badged
    // row, and the endpoint is persona-gated to match.
    const navCounts = useNavCounts(BADGED_FRONTENDS.includes(frontend));

    // Every LIVE badge, keyed by sidebar row. A row with no entry falls back to
    // its static `badge` config. The map replaces a chain of `section.key ===`
    // branches, so a fifth badge is a line here rather than a fifth branch in
    // the render loop.
    const liveBadges: Record<string, { count: number; tone: SidebarBadgeTone } | undefined> = {
        agents: badgeOf(reviewItemCount, 'default'),
        'overdrawn-cash': badgeOf(navCounts.overdrawnCash, 'alert'),
        'corporate-actions': badgeOf(navCounts.corporateActions, 'alert'),
        onboarding: badgeOf(navCounts.onboarding, 'alert'),
        'peer-reviews': badgeOf(navCounts.peerReviews, 'alert'),
        'final-approvals': badgeOf(navCounts.finalApprovals, 'alert'),
    };

    // Persona switch (ADR-0004 / issue #100): navigate to the new frontend
    // and let `FrontendLayout`'s sync effect call `/api/auth/persona`. We
    // route the audit + cookie update through that single point so a
    // deep-link entry (typing /comply) and a sidebar click share the
    // exact same code path — and the cookie is never out of step with
    // the visible URL.
    const handleSwitchFrontend = (newFrontend: StaffShellKey) => {
        track('interaction', { details: { action: 'persona_switch', to: newFrontend } });
        router.push(`/${newFrontend}`);
    };

    // The IM book switch. An assistant assigned to several IMs reads
    // one book at a time, so the Desk button becomes one button for each IM,
    // labelled with their desk code ("JBD", "WD").
    //
    // The highlighted button moves at once, and the server call reconciles the
    // session behind it. Every domain query key is book-blind, so the cached
    // payloads of the previous book must go — the same cache wall a persona
    // switch raises, for the same reason.
    //
    // `runBookSwitch` holds the order, and the comment on it holds the reasons:
    // the wall waits for the server, and the wall resets rather than removes.
    //
    // The switch is awaited here, and it is not fire-and-forget. The reader is
    // held for the length of the request: the switcher row is disabled, and the
    // move to `/desk` from another shell happens only after the server answers.
    // A press that only starts a request lost the book of a reader who opened
    // another Desk page at once, because the navigation cancelled the request.
    const handleSwitchBook = async (userId: string) => {
        if (userId !== activeActingForUserId) {
            track('interaction', { details: { action: 'acting_for_switch', to: userId } });
            setBookSwitchInFlight(true);
            try {
                await runBookSwitch(userId, {
                    paint: setActingForUserId,
                    request: switchActingFor,
                    queryClient,
                    refresh,
                    onRefused: () =>
                        pushToast({ kind: 'error', message: 'Could not switch to that IM book.' }),
                });
            } finally {
                setBookSwitchInFlight(false);
            }
        }
        if (frontend !== 'desk') router.push('/desk');
    };

    // One render of the switcher buttons, shared by the row in the panel and
    // by the popover of the rail. The click handlers are the same audited
    // switch paths in both places. A label stays on one line: a label that is
    // too long for its button ends in an ellipsis, and the title shows the
    // full name.
    const renderSwitcherButtons = (onChoose?: () => void, vertical = false) =>
        switcherButtons.map((button) => (
            <button
                key={button.key}
                type="button"
                aria-label={`Switch to ${button.title}`}
                title={`Switch to ${button.title}`}
                disabled={bookSwitchInFlight}
                onClick={() => {
                    if (button.actingForUserId) {
                        void handleSwitchBook(button.actingForUserId);
                    } else {
                        handleSwitchFrontend(button.frontend);
                    }
                    onChoose?.();
                }}
                className={cn(
                    'h-6 truncate rounded-[6px] text-[11px] font-medium transition-colors',
                    vertical ? 'w-full px-2 text-left' : 'min-w-0 flex-1 px-1',
                    bookSwitchInFlight && 'pointer-events-none',
                    button.active
                        ? 'bg-[rgb(var(--sidebar-accent))] text-white'
                        : 'bg-[rgb(var(--color-text-primary)/0.04)] text-[rgb(var(--color-text-secondary))] hover:bg-[rgb(var(--color-text-primary)/0.08)] hover:text-[rgb(var(--color-text-primary))]',
                )}
            >
                {vertical ? button.title.replace(/^the book of /, '') : button.label}
            </button>
        ));

    const activeSwitcherButton = switcherButtons.find((button) => button.active);
    // The rail square shows a short form of the active label: a code of three
    // letters or fewer ("JBD", "Ops") as it is, a longer name as its initial.
    const railSwitcherLabel = activeSwitcherButton
        ? activeSwitcherButton.label.length <= 3
            ? activeSwitcherButton.label
            : activeSwitcherButton.label.charAt(0)
        : FRONTENDS[frontend].shortLabel.charAt(0);

    const brandContent = (
        <div className="flex flex-col gap-1.5">
            <div
                className={cn(
                    'flex h-8 items-center gap-2',
                    collapsed ? 'justify-center' : 'px-1.5',
                )}
            >
                {collapsed ? (
                    <Image
                        src={getSeptimaMarkPath(resolvedTheme)}
                        alt="Septima"
                        height={20}
                        className="h-5 w-auto flex-none"
                    />
                ) : (
                    <>
                        <Image
                            src={getSeptimaWordmarkPath(resolvedTheme)}
                            alt="Septima"
                            height={16}
                            className="h-4 w-auto flex-none"
                        />
                        <span
                            aria-hidden
                            className="h-3.5 w-px flex-none bg-[rgb(var(--color-divider))]"
                        />
                        <span className="truncate text-[13px] font-medium leading-5 text-[rgb(var(--color-text-secondary))]">
                            {FRONTENDS[frontend].shortLabel}
                        </span>
                    </>
                )}
            </div>

            {/* View switcher — only the user's granted views (ADR-0004).
                An assistant of several IMs gets one Desk button for each
                book instead of the single "Desk" button, so
                the row reads "JBD | WD | Ops | View". A book switch holds
                the whole row until the server answers. The colours do not
                change, so the optimistic highlight stays on the book the
                reader chose. `pointer-events-none` goes with `disabled`: it
                stops the hover colours, and it lets the wait cursor of the
                row show through each button. */}
            {switcherButtons.length > 1 &&
                (collapsed ? (
                    <SidebarMenu
                        label="Switch view"
                        className="flex justify-center"
                        panelClassName={cn(
                            'left-full top-0 ml-2 w-44',
                            bookSwitchInFlight && 'cursor-wait',
                        )}
                        renderTrigger={({ open, toggle }) => (
                            <button
                                type="button"
                                onClick={toggle}
                                aria-haspopup="dialog"
                                aria-expanded={open}
                                aria-label={`Switch view. Current: ${activeSwitcherButton?.title ?? FRONTENDS[frontend].label}`}
                                title={activeSwitcherButton?.title ?? FRONTENDS[frontend].label}
                                className="grid h-7 w-7 place-items-center rounded-[6px] bg-[rgb(var(--sidebar-accent))] text-[10px] font-semibold text-white outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.28)]"
                            >
                                {railSwitcherLabel}
                            </button>
                        )}
                    >
                        {(close) => (
                            <div className="flex flex-col gap-1" aria-busy={bookSwitchInFlight}>
                                {renderSwitcherButtons(close, true)}
                            </div>
                        )}
                    </SidebarMenu>
                ) : (
                    <div
                        className={cn('flex gap-1 px-1', bookSwitchInFlight && 'cursor-wait')}
                        aria-busy={bookSwitchInFlight}
                    >
                        {renderSwitcherButtons()}
                    </div>
                ))}
        </div>
    );

    // The user row: the avatar and the name. The email, the agent badge and
    // the sign-out button are in its menu.
    const userCard = user && (
        <SidebarMenu
            label="Account"
            panelClassName={
                collapsed
                    ? 'left-full bottom-0 ml-2 w-60'
                    : 'bottom-full left-0 mb-1.5 w-full min-w-52'
            }
            renderTrigger={({ open, toggle }) => (
                <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="dialog"
                    aria-expanded={open}
                    aria-label={collapsed ? `Account: ${user.name ?? 'Signed in'}` : undefined}
                    title={collapsed ? (user.name ?? 'Signed in') : undefined}
                    className={cn(
                        'flex h-8 items-center gap-2 rounded-[6px] outline-none transition-colors',
                        'hover:bg-[rgb(var(--color-sidebar-hover)/0.05)] focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.28)]',
                        collapsed ? 'w-8 justify-center' : 'w-full px-1.5',
                    )}
                >
                    <span className="grid h-5 w-5 flex-none place-items-center rounded-[5px] bg-[rgb(var(--sidebar-accent))] text-[9.5px] font-semibold text-white">
                        {initialsOf(user.name)}
                    </span>
                    {!collapsed && (
                        <span className="flex-1 truncate text-left text-[12.5px] font-medium leading-4 text-[rgb(var(--color-text-primary))]">
                            {user.name ?? 'Signed in'}
                        </span>
                    )}
                </button>
            )}
        >
            {(close) => (
                <>
                    <div className="flex flex-col px-2 py-1">
                        <span className="truncate text-[12.5px] font-medium text-[rgb(var(--color-text-primary))]">
                            {user.name ?? 'Signed in'}
                        </span>
                        {user.email && (
                            <span className="truncate text-annotation text-[rgb(var(--color-text-secondary))]">
                                {user.email}
                            </span>
                        )}
                    </div>
                    {/* Opening the agent panel closes this menu. */}
                    <div onClick={close}>
                        <AgentBadge frontend={frontend === 'admin' ? 'global' : frontend} />
                    </div>
                    <div className="border-t border-[rgb(var(--color-divider))] pt-1">
                        <SignOutButton />
                    </div>
                </>
            )}
        </SidebarMenu>
    );

    return (
        <Sidebar
            mobileOpen={mobileOpen}
            className={SIDEBAR_ACCENT[frontend]}
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            shortcutLabel={shortcutLabel}
            brandContent={brandContent}
            userCard={userCard || undefined}
        >
            <div className={cn('flex flex-col', collapsed ? 'gap-1' : 'gap-2')}>
                {groupedSections.map((group, index) => (
                    <section key={group.label} aria-label={group.label}>
                        {collapsed ? (
                            index > 0 && (
                                <div
                                    role="separator"
                                    className="mx-2 mb-1 h-px bg-[rgb(var(--color-divider))]"
                                />
                            )
                        ) : (
                            <div className="flex h-5 items-center px-2 text-[10.5px] font-medium leading-4 text-[rgb(var(--color-text-tertiary))]">
                                {group.label}
                            </div>
                        )}
                        <div className="flex flex-col gap-px">
                            {group.items.map((section) => {
                                // A work-in-progress page is flagged "WIP" over
                                // any other badge; otherwise a row takes its
                                // live count, and falls back to its static
                                // badge config when it has none.
                                const live = liveBadges[section.key];
                                const badge = section.wip ? 'WIP' : (live?.count ?? section.badge);
                                return (
                                    <SidebarRow
                                        key={section.key}
                                        icon={getIcon(section.icon)}
                                        label={section.label}
                                        href={section.href}
                                        badge={badge}
                                        badgeTone={section.wip ? 'default' : live?.tone}
                                        aliases={section.aliases}
                                        collapsed={collapsed}
                                    />
                                );
                            })}
                        </div>
                    </section>
                ))}
            </div>
        </Sidebar>
    );
}
